import {
  Box,
  Stack,
  Typography,
  SwipeableDrawer,
  IconButton,
  Radio,
  Chip,
  Button,
  Divider
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";

import type { MarketplaceTemplate } from "@/data/mock-data";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";

// ---- Public types ---------------------------------------------------------

/**
 * Sort keys for the Marketplace. Each key sorts "best first" — the
 * direction is
 * baked into the comparator so the UI only needs a single-select.
 */
export type MarketplaceSortKey =
  | "cumulativeReturn"
  | "ar"
  | "maxDrawdown"
  | "sharpe"
  | "calmar"
  | "listedDate"
  | "oosDate"
  | "stdDev"
  | "trailing1M"
  | "trailing3M";

export const MARKETPLACE_SORT_OPTIONS: ReadonlyArray<{
  key: MarketplaceSortKey;
  label: string;
}> = [
  { key: "cumulativeReturn", label: "Cumulative Return" },
  { key: "ar", label: "Annualized Return" },
  { key: "maxDrawdown", label: "Max Drawdown" },
  { key: "sharpe", label: "Sharpe Ratio" },
  { key: "calmar", label: "Calmar Ratio" },
  { key: "listedDate", label: "Newest" },
  { key: "oosDate", label: "Recent OOS Date" },
  { key: "stdDev", label: "Standard Deviation" },
  { key: "trailing1M", label: "Trailing 1 Month Return" },
  { key: "trailing3M", label: "Trailing 3 Month Return" }
];

/**
 * Recovery-time filter — how long the strategy took to climb back to
 * a new high after its worst drawdown. `any` disables the filter.
 */
export type RecoveryTimeFilter = "any" | "6m" | "1y";

export interface MarketplaceFilterState {
  sort: MarketplaceSortKey;
  /** Recovery-time ceiling. */
  recoveryTime: RecoveryTimeFilter;
  /** Category tags the template must match (at least one). Empty = all. */
  categories: string[];
}

export const DEFAULT_MARKETPLACE_FILTER: MarketplaceFilterState = {
  sort: "cumulativeReturn",
  recoveryTime: "any",
  categories: []
};

/** Count of non-default filters — drives the "Filters · N" badge. */
export function activeFilterCount(f: MarketplaceFilterState): number {
  let n = 0;
  if (f.recoveryTime !== "any") n += 1;
  n += f.categories.length;
  return n;
}

// ---- Apply (filter + sort) ------------------------------------------------

/**
 * Apply a filter state to the template list — filters by recovery
 * time + categories, then sorts best-first by the chosen metric.
 */
export function applyMarketplaceFilters(
  templates: readonly MarketplaceTemplate[],
  f: MarketplaceFilterState,
  search: string
): MarketplaceTemplate[] {
  const q = search.trim().toLowerCase();
  const ceiling =
    f.recoveryTime === "6m" ? 6 : f.recoveryTime === "1y" ? 12 : Infinity;

  const filtered = templates.filter((t) => {
    if (t.recoveryTimeMonths > ceiling) return false;
    if (
      f.categories.length > 0 &&
      !f.categories.some((c) => t.tags.includes(c))
    ) {
      return false;
    }
    if (q) {
      const hay = `${t.name} ${t.creator} ${t.description}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    switch (f.sort) {
      // Lower is better → ascending.
      case "maxDrawdown":
      case "stdDev":
        return a[f.sort] - b[f.sort];
      // Most recent first.
      case "listedDate":
      case "oosDate":
        return b[f.sort].localeCompare(a[f.sort]);
      // Higher is better → descending.
      default:
        return (b[f.sort] as number) - (a[f.sort] as number);
    }
  });
  return sorted;
}

// ---- Sort sheet -----------------------------------------------------------

interface SortSheetProps {
  open: boolean;
  value: MarketplaceSortKey;
  onChange: (v: MarketplaceSortKey) => void;
  onClose: () => void;
}

/**
 * "Sort by" bottom sheet — single-select radio list. Picking an
 * option applies immediately and closes the sheet.
 */
export function MarketplaceSortSheet({
  open,
  value,
  onChange,
  onClose
}: SortSheetProps) {
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          maxHeight: "80vh",
          ...desktopBottomDrawerPaperSx
        }
      }}
    >
      <SheetHeader title="Sort by" onClose={onClose} />
      <Box sx={{ overflowY: "auto" }}>
        {MARKETPLACE_SORT_OPTIONS.map((o) => (
          <Box
            key={o.key}
            role="button"
            tabIndex={0}
            onClick={() => {
              onChange(o.key);
              onClose();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 1.25,
              cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" }
            }}
          >
            <Typography variant="body1" sx={{ flex: 1 }}>
              {o.label}
            </Typography>
            <Radio checked={value === o.key} size="small" />
          </Box>
        ))}
      </Box>
    </SwipeableDrawer>
  );
}

// ---- Filter sheet ---------------------------------------------------------

interface FilterSheetProps {
  open: boolean;
  value: MarketplaceFilterState;
  /** All category tags available across the template set. */
  allCategories: string[];
  onChange: (next: MarketplaceFilterState) => void;
  onClose: () => void;
}

const RECOVERY_OPTIONS: ReadonlyArray<{
  key: RecoveryTimeFilter;
  label: string;
}> = [
  { key: "any", label: "Any" },
  { key: "6m", label: "Recovers within 6 months" },
  { key: "1y", label: "Recovers within 1 year" }
];

/**
 * Filters bottom sheet — Recovery Time (≤6m / ≤1y) and category
 * multi-select. Changes apply live; "Reset" clears everything but the
 * sort. Sort + filters live in separate sheets so each stays short
 * on a phone.
 */
export function MarketplaceFilterSheet({
  open,
  value,
  allCategories,
  onChange,
  onClose
}: FilterSheetProps) {
  const toggleCategory = (c: string) => {
    const next = value.categories.includes(c)
      ? value.categories.filter((x) => x !== c)
      : [...value.categories, c];
    onChange({ ...value, categories: next });
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          maxHeight: "80vh",
          ...desktopBottomDrawerPaperSx
        }
      }}
    >
      <SheetHeader title="Filters" onClose={onClose} />
      <Box sx={{ overflowY: "auto", px: 2, pb: 2 }}>
        {/* Recovery Time */}
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", fontWeight: 700 }}
        >
          Recovery Time
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          Time to recover from the largest peak-to-trough drop.
        </Typography>
        {RECOVERY_OPTIONS.map((o) => (
          <Box
            key={o.key}
            role="button"
            tabIndex={0}
            onClick={() => onChange({ ...value, recoveryTime: o.key })}
            sx={{
              display: "flex",
              alignItems: "center",
              py: 1,
              cursor: "pointer"
            }}
          >
            <Typography variant="body1" sx={{ flex: 1 }}>
              {o.label}
            </Typography>
            <Radio checked={value.recoveryTime === o.key} size="small" />
          </Box>
        ))}

        <Divider sx={{ my: 1.5 }} />

        {/* Categories */}
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", fontWeight: 700 }}
        >
          Categories
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: "wrap", gap: 1, mt: 1 }}
        >
          {allCategories.map((c) => {
            const on = value.categories.includes(c);
            return (
              <Chip
                key={c}
                label={c}
                clickable
                variant={on ? "filled" : "outlined"}
                onClick={() => toggleCategory(c)}
                sx={{
                  fontWeight: 600,
                  ...(on
                    ? {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.light" }
                      }
                    : {})
                }}
              />
            );
          })}
        </Stack>

        <Button
          fullWidth
          variant="text"
          onClick={() =>
            onChange({ ...value, recoveryTime: "any", categories: [] })
          }
          sx={{ mt: 2 }}
        >
          Reset filters
        </Button>
      </Box>
    </SwipeableDrawer>
  );
}

// ---- Shared sheet header --------------------------------------------------

function SheetHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 2, py: 1.5 }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <IconButton aria-label="Close" onClick={onClose} size="small">
        <CloseRounded />
      </IconButton>
    </Stack>
  );
}
